/* ============================================================
   Company profile — the deck, rendered from the product.

   This page began as a fifteen-slide PowerPoint. It is a page instead
   of a download for one reason, and it is the same reason the rest of
   this product exists: the deck asserted "status is computed, never
   stored" and "anything older than six months fails the build", in a
   frozen file that could do neither. It said "10 of 12 instruments in
   force" and "current as at 2 August 2026" — both true when written,
   neither self-correcting. A profile that argues against stale numbers
   has to be the first thing that cannot carry any.

   So every count here is computed at render from the same registers
   the tools use. Add a tool and this page says so. Let an instrument
   come into force and the count moves the morning it does. The
   scripts/check-data.mjs guard fails the build if a number on this
   page is written by hand rather than derived.

   It is in the main bundle rather than code-split, and that is a
   deliberate trade rather than an oversight: it costs every visitor
   about 4 KB gzipped for a page most will never open. The alternative
   is a dynamic import, which would make the profile a separate chunk
   the service worker does not precache — and this product promises to
   work offline at a border post with no signal. A marketing page that
   is the one thing that breaks offline would undercut the claim it
   exists to make. 4 KB is the cheaper of the two costs.

   What this page deliberately does NOT do is restate the method.
   Scoring formula, benchmark provenance, the duty order of assessment
   and the full register live on /about, and duplicating them here
   would create exactly the second source of truth this page exists to
   argue against. The profile is the narrative — market, practice,
   engagement — and it links to the workings.
   ============================================================ */

import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { REGULATIONS, statusOf } from '../data/regulations.js';
import { BENCHMARKS, BENCHMARK_AS_OF } from '../data/benchmarks.js';
import { DOMAINS } from '../data/domains.js';
import { TOOLS, TRACKS } from '../data/tools.js';
import { MARKET, COST_LAYERS, figureAge } from '../data/market.js';
import { longDate } from '../shared/format.js';
import { router } from '../shared/router.js';
import { PrintButton } from '../components/PrintButton.js';
import { EditionStrip } from '../components/EditionStrip.js';

export function ProfilePage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  /* ---- everything countable, counted ---- */
  const liveTools = TOOLS.filter((t) => t.status === 'live');
  const inForce = REGULATIONS.filter((r) => statusOf(r) === 'in-force').length;
  const corridors = Object.keys(BENCHMARKS).length;
  const questions = DOMAINS.reduce((n, d) => n + (d.questions?.length || 0), 0);
  const weightTotal = DOMAINS.reduce((n, d) => n + d.weight, 0);
  const byTrack = (id) => liveTools.filter((t) => t.track === id).length;

  const stats = [
    {
      figure: String(liveTools.length),
      label: 'tools live — all free, no account, installable and working offline after first load'
    },
    {
      figure: String(REGULATIONS.length),
      label: `regulatory instruments tracked — ${inForce} in force today, status computed not stored`
    },
    {
      figure: String(corridors),
      label: 'corridors benchmarked — every band carries its derivation, payload assumption and provenance'
    },
    {
      figure: '0',
      label: "bytes of user data transmitted — enforced by CSP, verifiable in the browser's network tab"
    }
  ];

  const statCards = stats
    .map(
      (s) => html`
        <div class="card" style="padding: var(--ca-space-4);">
          <div style="font-size: var(--ca-text-2xl); font-weight: var(--ca-weight-bold); color: var(--ca-text-primary);">
            ${s.figure}
          </div>
          <div style="font-size: var(--ca-text-xs); color: var(--ca-text-secondary); margin-top: var(--ca-space-1);">
            ${s.label}
          </div>
        </div>
      `
    )
    .join('');

  /* --ca-warning-text, not --ca-warning. The signal colour is tuned for
     fills and borders and lands at 3.76:1 on small text — below AA, and
     the focus/contrast sweep says so. A staleness warning nobody can
     read is the same defect as no warning.

     Market figures print their own age. A number whose publisher has
     since issued a new edition says so on screen rather than being
     quietly dropped — the reader can then judge it, which is the whole
     argument of the page. */
  const marketCards = MARKET.map((m) => {
    const age = figureAge(m);
    const note = age.pastCycle && !m.structural
      ? `<div style="font-size: var(--ca-text-xs); color: var(--ca-warning-text); margin-top: var(--ca-space-1);">
           A newer ${m.cadence} edition may be available — verify before relying on this.
         </div>`
      : '';
    const structural = m.structural && age.pastCycle
      ? `<div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: var(--ca-space-1);">
           A structural share rather than a moving figure; the date is shown because it is old,
           not because a fresher survey exists.
         </div>`
      : '';
    return html`
      <div class="card" style="margin-bottom: var(--ca-space-3);">
        <div style="font-size: var(--ca-text-xl); font-weight: var(--ca-weight-bold);">${m.stat}</div>
        <div style="font-weight: var(--ca-weight-medium); margin-bottom: var(--ca-space-1);">${m.label}</div>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin: 0;">${m.detail}</p>
        <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: var(--ca-space-2);">
          ${m.source} · as at ${longDate(m.asOf)}
        </div>
        ${raw(note)}${raw(structural)}
      </div>
    `;
  }).join('');

  const layerCards = COST_LAYERS.map((l) => {
    const tool = TOOLS.find((t) => t.id === l.tool);
    const cta = tool && tool.status === 'live'
      ? `<button class="link-inline" type="button" data-goto="${tool.path}">Compute it — ${tool.title} →</button>`
      : '';
    return html`
      <div class="card" style="margin-bottom: var(--ca-space-3);">
        <h3 style="font-size: var(--ca-text-base); margin-bottom: var(--ca-space-2);">${l.title}</h3>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-2);">
          ${l.body}
        </p>
        ${raw(cta)}
      </div>
    `;
  }).join('');

  const domainRows = DOMAINS.map(
    (d) => html`
      <tr>
        <td>${d.name}</td>
        <td style="text-align: right;">${d.weight}%</td>
        <td style="text-align: right;">${d.questions?.length ?? 0}</td>
      </tr>
    `
  ).join('');

  page.innerHTML = html`
    <header class="page-head">
      <p class="eyebrow">Company profile</p>
      <h1>Know what the corridor actually costs you.</h1>
      <p>
        Corridor logistics intelligence for Kenya and the EAC. Cost per tonne-kilometre, landed
        cost, demurrage exposure and every regulatory deadline that applies to you — computed in
        your browser, sourced and dated, and never sent anywhere.
      </p>
    </header>

    <div id="profile-body">
      <section class="section">
        <h2 class="section-title">The practice, in numbers</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          Every figure on this page is counted from the same registers the tools run on, at the
          moment you loaded it. None of them is typed.
        </p>
        <div class="grid grid-2">${raw(statCards)}</div>
        <p style="font-size: var(--ca-text-xs); color: var(--ca-text-secondary); margin-top: var(--ca-space-3);">
          Decision support, not professional advice — and honest about exactly where that line
          runs. See <button class="link-inline" type="button" data-goto="/about">Method &amp; sources</button>.
        </p>
      </section>

      <section class="section">
        <h2 class="section-title">The corridor, honestly</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          East Africa trades on some of the world's most expensive roads. The cost is not
          anecdote — it is measured, published, and still roughly double international best
          practice. Each figure below carries its source and the date it was published.
        </p>
        ${raw(marketCards)}
      </section>

      <section class="section">
        <h2 class="section-title">Where the money leaks</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          The headline freight rate is only the beginning. Four cost layers hide between the
          quoted rate and the true landed cost — and each one has a calculator here.
        </p>
        ${raw(layerCards)}
      </section>

      <section class="section">
        <h2 class="section-title">Two tracks. Which side of the problem are you on?</h2>
        <div class="grid grid-2">
          <div class="card">
            <h3 style="font-size: var(--ca-text-base);">${TRACKS.move.label} · ${TRACKS.move.caption}</h3>
            <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">${TRACKS.move.blurb}</p>
            <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
              ${byTrack('move')} tools live
            </div>
          </div>
          <div class="card">
            <h3 style="font-size: var(--ca-text-base);">${TRACKS.build.label} · ${TRACKS.build.caption}</h3>
            <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">${TRACKS.build.blurb}</p>
            <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
              ${byTrack('build')} tools live
            </div>
          </div>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">The Corridor Health Index</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); margin-bottom: var(--ca-space-3);">
          ${DOMAINS.length} domains, weighted, ${questions} questions in total. Each answer scores
          0–4; a domain's maturity is points over the maximum available on the questions you
          actually scored — skipping lowers the confidence figure, not the score.
        </p>
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr><th>Domain</th><th style="text-align:right;">Weight</th><th style="text-align:right;">Questions</th></tr>
            </thead>
            <tbody>${raw(domainRows)}</tbody>
            <tfoot>
              <tr><td><strong>Total</strong></td><td style="text-align:right;"><strong>${weightTotal}%</strong></td><td style="text-align:right;"><strong>${questions}</strong></td></tr>
            </tfoot>
          </table>
        </div>
        <p style="font-size: var(--ca-text-xs); color: var(--ca-text-secondary); margin-top: var(--ca-space-2);">
          The full scoring formula, including how an unanswered domain is handled, is set out in
          <button class="link-inline" type="button" data-goto="/about">Method &amp; sources</button>.
        </p>
      </section>

      <section class="section">
        <h2 class="section-title">Benchmarks and the register</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
          ${corridors} corridors are benchmarked, struck as at ${longDate(BENCHMARK_AS_OF)}; each
          band carries its derivation, payload assumption and provenance on screen. Benchmarks
          decay, and the product treats that as a fact rather than an embarrassment — anything
          older than six months fails the build. Passing the build is not the same as true this
          week: your actual entry, contract and carrier tariff govern.
        </p>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
          ${REGULATIONS.length} instruments are tracked against their effective dates, of which
          <strong>${inForce}</strong> are in force today. Status is computed from today's date,
          never stored — a hardcoded status is how a product asks whether you are ready for an
          obligation that lapsed eight months ago.
        </p>
        <div class="row" style="gap: var(--ca-space-2); flex-wrap: wrap;">
          <button class="btn btn-secondary" type="button" data-goto="/benchmark">See the benchmarks</button>
          <button class="btn btn-secondary" type="button" data-goto="/compliance">See the register</button>
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Privacy by architecture</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
          We cannot see your numbers, because there is nowhere for them to go. Every calculation
          runs in this browser; every diagnostic is stored on this device. The content security
          policy blocks every off-origin request — which is also why the site ships no web fonts
          and no icon CDN — and you can verify it in your browser's network tab.
        </p>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
          The honest corollary is that nothing can be recovered for you either. Export what
          matters: every tool that computes a result produces a board-ready PDF, including this
          page.
        </p>
      </section>

      <section class="section">
        <h2 class="section-title">What this cannot do</h2>
        <p style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary);">
          It is decision support, not professional advice, and does not replace a licensed
          customs agent, an advocate or an accountant. It is authoritative on Kenyan regulation
          only — corridors terminating in Kampala, Kigali or Dar es Salaam are modelled for cost
          and transit, but the law of those jurisdictions is not covered. It cannot see your
          actual entry, contract or carrier tariff, so every figure is only as good as what you
          typed. And it does not broker freight, lend, or move money, by design — the position
          is advisory, with no balance-sheet interest in the answer.
        </p>
      </section>

      <section class="section">
        <h2 class="section-title">Working with Kanda</h2>
        <ol style="font-size: var(--ca-text-sm); color: var(--ca-text-secondary); padding-left: var(--ca-space-4);">
          <li style="margin-bottom: var(--ca-space-2);">
            <strong>Measure.</strong> Run the corridor diagnostic, the landed-cost engine or the
            demurrage exposure check — free, in-browser, offline-capable. Export the PDF; the
            output is yours.
          </li>
          <li style="margin-bottom: var(--ca-space-2);">
            <strong>Reconcile.</strong> Bring your actual entry, contract and carrier tariff. The
            indicative figures are reconciled against your real transactions and the leaks worth
            fixing first are isolated.
          </li>
          <li>
            <strong>Advise.</strong> Advisory mandates on corridor strategy, licensing and
            greenfield build — capital stack with DSCR, structure across jurisdictions, people
            and place.
          </li>
        </ol>
        <div class="row" style="gap: var(--ca-space-2); flex-wrap: wrap;">
          <button class="btn btn-primary" type="button" data-goto="/scorecard">Start with the diagnostic</button>
          <button class="btn btn-secondary" type="button" data-goto="/about-us">Who we are</button>
        </div>
      </section>
    </div>
  `;

  /* Router navigation rather than <a href>, matching the rest of the
     app, and delegated so the markup above stays declarative. */
  page.addEventListener('click', (e) => {
    const target = e.target.closest('[data-goto]');
    if (target) router.navigate(target.dataset.goto);
  });

  page.appendChild(
    PrintButton({
      targetId: 'profile-body',
      title: 'Kanda Logistics Advisory — company profile',
      subtitle: 'Corridor cost intelligence, computed where your data stays',
      /* Built on click, not on mount, so the export is stamped with the
         day it was produced and the register state at that moment. This
         is the whole reason the profile is a page and not a slide deck:
         a PDF handed to a lender in November says what was true in
         November, and says on its face that it was computed then.

         A plain node rather than BoardPreamble, which is shaped around
         a computed result measured against a benchmark register. This
         document has no result and no basis of comparison, and
         borrowing that component would print empty headings — which
         read as "we checked and found nothing" rather than "this is a
         different kind of document". */
      buildPreamble: () => {
        const node = document.createElement('section');
        node.className = 'print-only board-preamble';
        const rows = [
          ['Prepared', longDate(new Date().toISOString().slice(0, 10))],
          ['Register', `${REGULATIONS.length} instruments tracked, ${inForce} in force on this date`],
          ['Benchmarks', `${corridors} corridors, struck as at ${longDate(BENCHMARK_AS_OF)}`],
          ['Tools', `${liveTools.length} live`],
          ['Basis', 'Counts computed at export from the product registers, not transcribed']
        ];
        node.innerHTML = html`
          <dl class="board-meta">
            ${raw(rows.map(([dt, dd]) => `<div><dt>${dt}</dt><dd>${dd}</dd></div>`).join(''))}
          </dl>
        `;
        return node;
      }
    })
  );

  page.appendChild(EditionStrip());
  return page;
}
